import React, {useCallback, useMemo} from 'react'
import styled from 'styled-components'
import {getBorder, getFontFamily, showSnackbar} from 'utils'
import {useTranslation} from 'i18n'
import {TreeMapFootprintOverlayType} from 'types'
import {ButtonIcon, Paragraph, ParsedText} from 'common/components'
import convertUnit from 'lib/unit'
import {FototreeFootprintProps} from './FototreeFootprintProps'

interface StyledIconProps {
  disabled?: boolean
}

const StyledContainer = styled.div`
  box-sizing: border-box;
  position: absolute;
  display: flex;
  align-items: center;
  background-color: ${({theme}) => theme.white_1};
  border-bottom: ${({theme}) => getBorder(1, 'solid', theme.white_4)};
  width: 100%;
  z-index: 5;
  top: 0;
  padding: ${convertUnit(6)} ${convertUnit(20)};
  gap: ${convertUnit(20)};
`

const StyledTitleContainer = styled.div`
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  margin-right: auto;
`

const StyledIcon = styled(ButtonIcon)<StyledIconProps>`
  cursor: ${({disabled}) => (disabled ? 'default' : 'pointer')};
  width: ${convertUnit(42)};
  height: ${convertUnit(42)};
  border-radius: ${convertUnit(22)};
  background-color: ${({theme}) => theme.white_1};
`

export default function FototreeFootprintHeader({
  stateOverlay,
  stateLoading,
  data,
  onLoadData,
}: FototreeFootprintProps) {
  const {translate} = useTranslation()
  const [overlay, setOverlay] = stateOverlay
  const loading = stateLoading[0]

  const handleClickFootprintList = useCallback(() => {
    if (!data.length) {
      showSnackbar(translate('tree:leafEmpty'))
    } else {
      const changeOverlay: TreeMapFootprintOverlayType =
        overlay === 'full' ? 'header' : 'full'
      setOverlay(changeOverlay)
    }
  }, [data.length, overlay, setOverlay, translate])

  const handleRenderIconBack = useMemo(
    () => (
      <StyledIcon
        hasShadow={false}
        iconType="back"
        iconColor="primary_5"
        onClick={() => {
          setOverlay('hidden')
          onLoadData()
        }}
      />
    ),
    [onLoadData, setOverlay],
  )

  const handleRenderTitle = useMemo(
    () => (
      <StyledTitleContainer>
        <Paragraph fontSize="l" fontWeight="medium" lineHeight={31}>
          {translate('tree:footprints')}
        </Paragraph>
        <Paragraph color="primary_5">
          <ParsedText
            parse={[
              {
                pattern: /<bold>.*?<\/bold>/,
                style: {fontFamily: getFontFamily('bold')},
                renderText: (text) => text.replace(/<bold>|<\/bold>/g, ''),
              },
            ]}>
            {translate('tree:footprintsCount', {count: data.length})}
          </ParsedText>
        </Paragraph>
      </StyledTitleContainer>
    ),
    [data.length, translate],
  )

  const handleRenderIconFootprintList = useMemo(
    () => (
      <StyledIcon
        hasShadow={false}
        disabled={loading}
        iconColor={loading ? 'gray_3' : 'primary_5'}
        iconType={overlay === 'full' ? 'map-marked' : 'file-name'}
        onClick={handleClickFootprintList}
      />
    ),
    [handleClickFootprintList, loading, overlay],
  )

  return (
    <StyledContainer>
      {handleRenderIconBack}
      {handleRenderTitle}
      {handleRenderIconFootprintList}
    </StyledContainer>
  )
}
